/**
 * Accessible form behaviour: inline validation on blur, an error summary on submit, character counters,
 * honeypot handling, and submission for `data-form="lead"` (/demo) and `data-form="security"` (/security).
 */
import { $, $$, closestTarget, requireElement } from '../../lib/dom';
import { readStorage, readStorageJson, STORAGE_KEYS } from '../../lib/storage';
import { isRecord } from '../../lib/guards';
import { buildFallbackMailto } from '../leads/delivery/email-fallback';
import { buildPayload, sendForm, type FormFields } from '../leads/delivery/send-form';
import { submitLead } from '../leads/lead-submission';
import { fieldLabel, legendText, validateField, validateGroup, type FormControl } from './field-errors';
import {
  setSubmitting,
  showDeliveryFailure,
  showProblems,
  showSecurityRequestSuccess,
  type FieldProblem,
} from './form-status';

const SECURITY_FORM_TYPE = 'Security information request';

/** Checkbox groups whose checked values are joined into one field. */
const MULTI_VALUE_FIELDS = ['review'];

const FIELD_SELECTOR =
  'input:not([type="hidden"]):not([name="website"]):not([type="checkbox"]):not([type="radio"]), select, textarea, input[type="checkbox"][required]';

const isFormControl = (el: unknown): el is FormControl =>
  el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement;

function validatedFields(form: HTMLFormElement): FormControl[] {
  return $$(FIELD_SELECTOR, form, HTMLElement)
    .filter(isFormControl)
    .filter((el) => !el.closest('[hidden]') && !el.closest('[data-group-required]'));
}

function collectFields(form: HTMLFormElement): Record<string, string> {
  const fields: Record<string, string> = {};
  new FormData(form).forEach((value, key) => {
    if (typeof value === 'string') fields[key] = value;
  });
  MULTI_VALUE_FIELDS.forEach((name) => {
    const values = $$(`input[name="${name}"]:checked`, form, HTMLInputElement).map((c) => c.value);
    if (values.length) fields[name] = values.join('; ');
  });
  fields.consent_at = new Date().toISOString();
  return fields;
}

function findProblems(form: HTMLFormElement): FieldProblem[] {
  const problems: FieldProblem[] = [];
  validatedFields(form).forEach((el) => {
    const message = validateField(el);
    if (message) problems.push({ id: el.id, label: fieldLabel(el), message });
  });
  $$('[data-group-required]', form).forEach((fieldset) => {
    const message = validateGroup(fieldset);
    if (message) problems.push({ id: fieldset.id, label: legendText(fieldset), message });
  });
  return problems;
}

/** Pre-fills the demo form message with the capacity calculator summary when the visitor came from there. */
function prefillFromCalculator(form: HTMLFormElement): void {
  const summary = readStorage('session', STORAGE_KEYS.calculatorPrefill);
  const context = readStorageJson('session', STORAGE_KEYS.leadContext);
  const message = $('#l-msg', form, HTMLTextAreaElement);
  if (summary && message && !message.value && isRecord(context) && context.cta_source === 'capacity_calculator') {
    message.value = summary;
    message.dispatchEvent(new Event('input'));
  }
}

function bindCounters(form: HTMLFormElement): void {
  $$('[data-counter]', form, HTMLTextAreaElement).forEach((textarea) => {
    const counter = document.getElementById(`${textarea.id}-count`);
    const limit = Number(textarea.dataset.counter);
    textarea.addEventListener('input', () => {
      if (!counter) return;
      counter.textContent = `${textarea.value.length} / ${limit}`;
      counter.classList.toggle('over', textarea.value.length >= limit);
    });
  });
}

function bindInlineValidation(form: HTMLFormElement): void {
  form.addEventListener('focusout', (event) => {
    const el = event.target;
    if (!isFormControl(el) || !validatedFields(form).includes(el)) return;
    if (el.value || (el instanceof HTMLInputElement && el.type === 'checkbox') || el.hasAttribute('aria-invalid'))
      validateField(el);
  });
  form.addEventListener('input', (event) => {
    if (isFormControl(event.target) && event.target.getAttribute('aria-invalid') === 'true')
      validateField(event.target);
  });
  form.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const group = closestTarget(event, '[data-group-required]');
    if (group?.hasAttribute('aria-invalid')) validateGroup(group);
    if (target instanceof HTMLInputElement && target.type === 'checkbox' && target.required) validateField(target);
  });
}

function bindErrorSummaryLinks(summary: HTMLElement): void {
  summary.addEventListener('click', (event) => {
    const link = closestTarget(event, '[data-focus]');
    if (!link) return;
    event.preventDefault();
    const target = document.getElementById(link.dataset.focus ?? '');
    const focusable = target?.matches('fieldset') ? $('input:not([type="hidden"])', target) : target;
    focusable?.focus();
  });
}

async function submitSecurityRequest(
  form: HTMLFormElement,
  summary: HTMLElement,
  fields: FormFields,
  isBot: boolean,
): Promise<void> {
  const restoreButton = setSubmitting(form, 'Sending…');
  try {
    if (!isBot) await sendForm(fields, SECURITY_FORM_TYPE);
  } catch {
    restoreButton(); // entered data is kept
    showDeliveryFailure(summary, {
      emailHref: buildFallbackMailto(buildPayload(fields, SECURITY_FORM_TYPE), SECURITY_FORM_TYPE),
      keptEntries: false,
    });
    return;
  }
  showSecurityRequestSuccess(form);
}

export function initForm(form: HTMLFormElement): void {
  const kind = form.dataset.form;
  if (kind !== 'lead' && kind !== 'security') return;
  const summary = requireElement('.err-summary', form);

  bindInlineValidation(form);
  bindCounters(form);
  bindErrorSummaryLinks(summary);
  if (kind === 'lead') prefillFromCalculator(form);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const problems = findProblems(form);
    if (problems.length) {
      showProblems(summary, problems);
      return;
    }
    summary.hidden = true;
    const honeypot = form.elements.namedItem('website');
    const isBot = honeypot instanceof HTMLInputElement && honeypot.value !== '';
    const fields = collectFields(form);
    void (kind === 'lead' ? submitLead(form, fields, isBot) : submitSecurityRequest(form, summary, fields, isBot));
  });
}
